import { useCallback, type ReactNode } from 'react';

import { Badge, type BadgeTone } from '@/shared/ui/badge';
import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The tenant's TEAM.
 *
 * The staff memberships the API holds, with their status, the functions they cover and the
 * skills they carry. An offboarded member is still a membership — they are counted, and they
 * are shown as offboarded rather than dropped, because "who used to work here" is a question
 * the dashboard should be able to answer.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'positive',
  offboarded: 'neutral',
};

export function TeamSummaryWidget({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.staff(), []);

  return (
    <DashboardWidgetFrame
      title="Team"
      load={load}
      className={className}
      isEmpty={(data) => data.total === 0}
      emptyTitle="No team members yet"
      emptyDescription="Staff memberships appear once someone joins this tenant's team."
    >
      {(data) => (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard label="Members" value={String(data.total)} />
            <StatCard label="Active" value={String(data.active)} />
            <StatCard
              label="Offboarded"
              value={String(data.offboarded)}
              tone={data.offboarded === 0 ? 'neutral' : 'warning'}
            />
          </div>

          <ul className="flex flex-col">
            {data.members.map((member) => (
              <li
                key={member.id}
                className="flex flex-col gap-1 border-b border-border py-2 last:border-b-0"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-xs text-content-muted">{member.id}</span>
                  <Badge tone={STATUS_TONE[member.status] ?? 'neutral'}>
                    {member.status === 'active' ? 'Active' : 'Offboarded'}
                  </Badge>
                </div>
                {member.functionCodes.length === 0 &&
                member.skillCodes.length === 0 ? null : (
                  <p className="text-xs text-content-muted">
                    {[
                      ...member.functionCodes.map((code) => `function: ${code}`),
                      ...member.skillCodes.map((code) => `skill: ${code}`),
                    ].join(' · ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
